// Lichte, dependency-vrije validatiehelpers (vervanger voor Zod in deze
// sandbox zonder npm-registrytoegang — zie README voor migratiepad).
// Gedeeld tussen client (inline validatie in formulieren) en server
// (autoritatieve validatie in de API-routes). Client-side validatie is dus
// nooit de enige controle.

export interface FieldResult {
  valid: boolean;
  message?: string;
}

const BE_POSTAL_CODE = /^[1-9][0-9]{3}$/;
// Belgische telefoonnummers: vast of mobiel, met of zonder +32/landcode, spaties/punten toegestaan.
const BE_PHONE = /^(?:\+32|0032|0)\s?[1-9](?:[\s./-]?\d){7,8}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const VAT_BE = /^BE0?\d{9,10}$/;

export function required(value: unknown, label: string): FieldResult {
  if (typeof value === "string" && value.trim().length === 0) {
    return { valid: false, message: `${label} is verplicht.` };
  }
  if (value === null || value === undefined) {
    return { valid: false, message: `${label} is verplicht.` };
  }
  return { valid: true };
}

export function isEmail(value: string): FieldResult {
  if (!value) return { valid: false, message: "E-mailadres is verplicht." };
  if (!EMAIL.test(value.trim())) {
    return { valid: false, message: "Vul een geldig e-mailadres in." };
  }
  return { valid: true };
}

export function emailsMatch(email: string, confirm: string): FieldResult {
  if (email.trim().toLowerCase() !== confirm.trim().toLowerCase()) {
    return { valid: false, message: "E-mailadressen komen niet overeen." };
  }
  return { valid: true };
}

export function isBelgianPhone(value: string): FieldResult {
  if (!value) return { valid: false, message: "Telefoonnummer is verplicht." };
  const cleaned = value.trim();
  if (!BE_PHONE.test(cleaned)) {
    return { valid: false, message: "Vul een geldig Belgisch telefoonnummer in (bv. 0470 12 34 56)." };
  }
  return { valid: true };
}

export function isBelgianPostalCode(value: string): FieldResult {
  if (!value) return { valid: false, message: "Postcode is verplicht." };
  if (!BE_POSTAL_CODE.test(value.trim())) {
    return { valid: false, message: "Vul een geldige Belgische postcode in (4 cijfers)." };
  }
  return { valid: true };
}

export function isBelgianVat(value: string): FieldResult {
  if (!value) return { valid: true }; // optioneel veld in de meeste flows
  if (!VAT_BE.test(value.replace(/[.\s]/g, ""))) {
    return { valid: false, message: "Vul een geldig Belgisch ondernemingsnummer in (BE0XXXXXXXXX)." };
  }
  return { valid: true };
}

export function inRange(value: number, min: number, max: number, label: string): FieldResult {
  if (Number.isNaN(value)) return { valid: false, message: `${label} is verplicht.` };
  if (value < min || value > max) {
    return { valid: false, message: `${label} moet tussen ${min} en ${max} liggen.` };
  }
  return { valid: true };
}

export function minLength(value: string, min: number, label: string): FieldResult {
  if (!value || value.trim().length < min) {
    return { valid: false, message: `${label} moet minstens ${min} tekens bevatten.` };
  }
  return { valid: true };
}

// Eenvoudige HTML-sanitisatie voor vrije-tekstvelden die opgeslagen en later
// (bv. in e-mails of admin) getoond worden. Strip tags i.p.v. escapen van
// weergave, zodat opgeslagen data altijd platte tekst is.
const CONTROL_CHARS = new RegExp("[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]", "g");

export function sanitizePlainText(value: string, maxLength = 4000): string {
  return value
    .replace(/<[^>]*>/g, "")
    .replace(CONTROL_CHARS, "")
    .trim()
    .slice(0, maxLength);
}

// Anti-spam: honeypot-veld moet leeg zijn, en het formulier mag niet
// "onmenselijk snel" (< 2s) verzonden zijn na render.
export function passesSpamHeuristics(honeypot: string | undefined, renderedAt: number | undefined): FieldResult {
  if (honeypot && honeypot.trim().length > 0) {
    return { valid: false, message: "Aanvraag geweigerd." };
  }
  if (renderedAt && Date.now() - renderedAt < 1500) {
    return { valid: false, message: "Aanvraag geweigerd, probeer opnieuw." };
  }
  return { valid: true };
}

export const ALLOWED_UPLOAD_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "docx"];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB per bestand
export const MAX_UPLOAD_FILES = 6;

export function isAllowedUploadFile(filename: string, sizeBytes: number): FieldResult {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
    return { valid: false, message: `Bestandstype .${ext} is niet toegestaan.` };
  }
  if (sizeBytes > MAX_UPLOAD_BYTES) {
    return { valid: false, message: "Bestand is groter dan 10MB." };
  }
  return { valid: true };
}
